import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "../../lib/dayjs";
import {
  createPrescriptionTemplate,
  deletePrescriptionTemplate,
  listPrescriptionTemplates,
  updatePrescriptionTemplate,
} from "../../services/prescriptionTemplates.service";
import { IPrescriptionTemplate, TemplateFieldType } from "../../interfaces/IPrescriptionTemplate";
import { ROUTES } from "../../router/constants";

const TYPE_TABS: { value: TemplateFieldType; label: string }[] = [
  { value: "PRESCRIPTION", label: "Prescrição" },
  { value: "DIAGNOSIS", label: "Hipóteses Diagnósticas" },
  { value: "THERAPEUTIC_PLAN", label: "Conduta" },
];

export function PrescriptionTemplatesManager() {
  const navigate = useNavigate();

  const [type, setType] = useState<TemplateFieldType>("PRESCRIPTION");
  const [templates, setTemplates] = useState<IPrescriptionTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState<IPrescriptionTemplate | null>(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [updating, setUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      try {
        const data = await listPrescriptionTemplates(type);
        if (!ignore) setTemplates(data);
      } catch {
        if (!ignore) toast.error("Não foi possível carregar os modelos.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [type]);

  const handleOpenCreate = () => {
    setNewTitle("");
    setNewContent("");
    setCreateOpen(true);
  };

  const handleConfirmCreate = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;
    setSaving(true);
    try {
      const created = await createPrescriptionTemplate({ type, title: newTitle.trim(), content: newContent });
      setTemplates((prev) => [created, ...prev]);
      toast.success("Modelo criado com sucesso.");
      setCreateOpen(false);
    } catch {
      toast.error("Não foi possível criar o modelo.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetail = (tpl: IPrescriptionTemplate) => {
    setDetail(tpl);
    setEditing(false);
    setEditTitle(tpl.title);
    setEditContent(tpl.content);
  };

  const handleCloseDetail = () => {
    setDetail(null);
    setEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!detail || !editTitle.trim() || !editContent.trim()) return;
    setUpdating(true);
    try {
      const updated = await updatePrescriptionTemplate(detail.id, { title: editTitle.trim(), content: editContent });
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setDetail(updated);
      setEditing(false);
      toast.success("Modelo atualizado.");
    } catch {
      toast.error("Não foi possível atualizar o modelo.");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deletePrescriptionTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (detail?.id === id) handleCloseDetail();
      toast.success("Modelo excluído.");
    } catch {
      toast.error("Não foi possível excluir o modelo.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton size="small" onClick={() => navigate(ROUTES.MY_MEDICAL_RECORDS)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700} color="#133859">
          Gerenciar Modelos
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Modelos reutilizáveis para preencher rapidamente prescrição, hipóteses diagnósticas e conduta.
      </Typography>

      <Tabs
        value={type}
        onChange={(_, v) => setType(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {TYPE_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ backgroundColor: "#133859" }}
          size="small"
        >
          Novo modelo
        </Button>
      </Stack>

      {loading && (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent>
                <Skeleton variant="text" width={220} height={24} />
                <Skeleton variant="text" width="80%" />
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {!loading && templates.length === 0 && (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              Nenhum modelo salvo para {TYPE_TABS.find((t) => t.value === type)?.label.toLowerCase()} ainda.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && templates.length > 0 && (
        <Stack spacing={2}>
          {templates.map((tpl) => (
            <Card
              key={tpl.id}
              sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 }, transition: "box-shadow .2s" }}
              onClick={() => handleOpenDetail(tpl)}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={700} color="#133859" sx={{ mb: 0.5 }}>
                      {tpl.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                    >
                      {tpl.content}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(tpl.id); }}
                    disabled={deletingId === tpl.id}
                  >
                    {deletingId === tpl.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* ── Dialog: novo modelo ─────────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo modelo — {TYPE_TABS.find((t) => t.value === type)?.label}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus fullWidth label="Nome do modelo" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            <TextField fullWidth multiline minRows={4} label="Conteúdo" value={newContent} onChange={(e) => setNewContent(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmCreate}
            disabled={saving || !newTitle.trim() || !newContent.trim()}
            sx={{ backgroundColor: "#133859" }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: detalhes / edição ───────────────────────────────────────── */}
      <Dialog open={Boolean(detail)} onClose={handleCloseDetail} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Editar modelo" : "Detalhes do modelo"}</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {editing ? (
                <>
                  <TextField autoFocus fullWidth label="Nome do modelo" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                  <TextField fullWidth multiline minRows={4} label="Conteúdo" value={editContent} onChange={(e) => setEditContent(e.target.value)} />
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" fontWeight={700} color="#133859">{detail.title}</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{detail.content}</Typography>
                  <Stack direction="row" spacing={3}>
                    <Typography variant="caption" color="text.secondary">
                      Criado em {dayjs(detail.createdAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Atualizado em {dayjs(detail.updatedAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} disabled={updating}>Cancelar</Button>
              <Button
                variant="contained"
                startIcon={updating ? <CircularProgress size={14} /> : <SaveIcon />}
                onClick={handleSaveEdit}
                disabled={updating || !editTitle.trim() || !editContent.trim()}
                sx={{ backgroundColor: "#133859" }}
              >
                {updating ? "Salvando..." : "Salvar"}
              </Button>
            </>
          ) : (
            <>
              <Button
                color="error"
                startIcon={deletingId === detail?.id ? <CircularProgress size={14} /> : <DeleteOutlineIcon />}
                onClick={() => detail && handleDelete(detail.id)}
                disabled={deletingId === detail?.id}
              >
                Excluir
              </Button>
              <Button startIcon={<EditIcon />} onClick={() => setEditing(true)}>Editar</Button>
              <Button onClick={handleCloseDetail} variant="contained" sx={{ backgroundColor: "#133859" }}>Fechar</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
